import React, { useState } from 'react';
import { AlertTriangle, Check, Info, X } from 'lucide-react';
import { useAccessTheme } from '../useAccessTheme';
import { CapabilityCheckList, ProviderStatusBadge } from './ProviderBadges';
import { EntraDetailGrid, EntraRadioOption, EntraSection } from './EntraWizardUi';
import { testEntraConnection } from './entraConnectionService';
import type { EntraWizardState } from './entraWizardTypes';

interface EntraConnectionStepProps {
  state: EntraWizardState;
  onChange: (partial: Partial<EntraWizardState>) => void;
}

function InstructionModal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  const t = useAccessTheme();
  return (
    <div className={'fixed inset-0 flex items-center justify-center p-4 ' + t.overlay} style={{ zIndex: 220 }}>
      <div className={`rounded-xl shadow-xl w-full max-w-md overflow-hidden border ${t.panel}`}>
        <div className={'flex items-center justify-between px-5 py-4 border-b ' + t.header}>
          <h4 className={`text-base font-semibold ${t.title}`}>{title}</h4>
          <button type="button" onClick={onClose} className={'p-1.5 rounded-lg ' + t.closeBtn}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-2">{children}</div>
        <div className={'px-5 py-4 border-t flex justify-end ' + t.footer}>
          <button type="button" onClick={onClose} className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnSecondary}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const EntraConnectionStep: React.FC<EntraConnectionStepProps> = ({ state, onChange }) => {
  const t = useAccessTheme();
  const [showSetup, setShowSetup] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [localError, setLocalError] = useState('');

  const canTest = Boolean(state.tenantId.trim() && state.clientId.trim() && state.clientSecret.trim());
  const labelCls = 'block text-xs font-medium mb-1 ' + t.muted;
  const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm outline-none ' + t.input;

  const runTest = async () => {
    if (!canTest || state.testStatus === 'testing') return;
    setLocalError('');
    onChange({ testStatus: 'testing', testMessage: '', connected: false });
    try {
      const result = await testEntraConnection({
        tenantId: state.tenantId.trim(),
        clientId: state.clientId.trim(),
        clientSecret: state.clientSecret,
        secretExpiration: state.secretExpiration || undefined,
      });
      const success = result.status === 'success' && Boolean(result.connectionId);
      onChange({
        testStatus: success ? 'success' : result.status === 'success' ? 'auth_failed' : result.status,
        testMessage: success
          ? result.message || ''
          : result.connectionId
            ? result.message || ''
            : result.message || 'Connection test succeeded but the directory connection was not saved.',
        connected: success,
        connectionId: result.connectionId || '',
        organization: result.organization || '',
        microsoftTenantId: result.microsoftTenantId || state.tenantId.trim(),
        clientId: result.applicationClientId || state.clientId.trim(),
        configuredBy: result.configuredBy || '',
        connectedAt: result.connectedAt || '',
        graphAccessVerified: success,
        graphAccess: result.graphAccess,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Connection test failed';
      setLocalError(message);
      onChange({
        testStatus: 'auth_failed',
        testMessage: message,
        connected: false,
        graphAccessVerified: false,
      });
    }
  };

  const resetToForm = () => {
    onChange({ testStatus: 'idle', testMessage: '', connected: false, graphAccessVerified: false });
    setLocalError('');
  };

  if (state.testStatus === 'success') {
    return (
      <>
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 ${t.border}`}>
          <Check className={`w-4 h-4 ${t.toggleOn}`} />
          <span className={`text-sm font-medium ${t.title}`}>Connection successful</span>
        </div>
        <EntraSection title="Connection">
          <EntraDetailGrid
            items={[
              { label: 'Status', value: <ProviderStatusBadge status="connected" /> },
              { label: 'Organization', value: state.organization || '—' },
              { label: 'Microsoft Tenant ID', value: state.microsoftTenantId || state.tenantId },
              { label: 'Application (Client) ID', value: state.clientId },
              { label: 'Authentication', value: 'Client Secret' },
              { label: 'Connected on', value: state.connectedAt || '—' },
              { label: 'Configured by', value: state.configuredBy || '—' },
              { label: 'Graph Access', value: 'Verified' },
            ]}
          />
        </EntraSection>
        <EntraSection title="Microsoft Graph Access">
          <ul className="space-y-2">
            {[
              { label: 'Users', check: state.graphAccess.users },
              { label: 'Groups', check: state.graphAccess.groups },
              { label: 'Group memberships', check: state.graphAccess.groupMemberships },
            ].map((item) => (
              <li key={item.label} className="flex items-start gap-2 text-sm">
                <Check className={`w-4 h-4 mt-0.5 ${t.toggleOn}`} />
                <span>
                  <span className={`block font-medium ${t.title}`}>{item.label}</span>
                  <span className={t.muted}>{item.check.message || 'Access verified'}</span>
                </span>
              </li>
            ))}
          </ul>
        </EntraSection>
        <button type="button" onClick={resetToForm} className={'px-3 py-1.5 rounded-md text-sm font-medium ' + t.btnGhost}>
          Edit credentials
        </button>
      </>
    );
  }

  return (
    <>
      <EntraSection
        title="Connection"
        description="Connect Selfic to your Microsoft Entra tenant using an App Registration. You will need an App Registration configured in Microsoft Entra ID with the required Microsoft Graph permissions."
      >
        <button type="button" onClick={() => setShowSetup(true)} className={'text-sm font-medium ' + t.accentText}>
          View setup instructions
        </button>
      </EntraSection>

      <EntraSection title="Microsoft Entra Details">
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Tenant ID *</label>
            <input
              type="text"
              name="entra-directory-tenant-id"
              value={state.tenantId}
              onChange={(e) => onChange({ tenantId: e.target.value })}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className={inputCls}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            <p className={`text-xs mt-1 ${t.muted}`}>The Directory (tenant) ID from Microsoft Entra.</p>
          </div>
          <div>
            <label className={labelCls}>Application (Client) ID *</label>
            <input
              type="text"
              name="entra-application-client-id"
              value={state.clientId}
              onChange={(e) => onChange({ clientId: e.target.value })}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className={inputCls}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            <p className={`text-xs mt-1 ${t.muted}`}>The Application (client) ID of the App Registration.</p>
          </div>
          <p className={`text-xs ${t.muted}`}>These two values are required for the client-credentials flow.</p>
        </div>
      </EntraSection>

      <EntraSection title="Authentication" description="Choose how Selfic should authenticate the application.">
        <div className="space-y-2">
          <EntraRadioOption
            name="entraAuth"
            value="client_secret"
            checked
            title="Client Secret"
            onChange={() => undefined}
          />
          <label className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 opacity-60 ${t.border}`}>
            <input type="radio" name="entraAuth" disabled className="mt-1" />
            <span>
              <span className={`block text-sm font-medium ${t.title}`}>Certificate</span>
              <span className={`block text-xs mt-0.5 ${t.muted}`}>Certificate support will be added later.</span>
            </span>
          </label>
        </div>

        <div className="mt-4">
          <label className={labelCls}>Client Secret *</label>
          <input
            type="text"
            name="entra-client-secret-value"
            value={state.clientSecret}
            onChange={(e) => onChange({ clientSecret: e.target.value })}
            onInput={(e) => onChange({ clientSecret: (e.target as HTMLInputElement).value })}
            placeholder="Enter the secret value"
            className={inputCls}
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
          />
          <p className={`text-xs mt-1 ${t.muted}`}>
            The secret value created under: Microsoft Entra → App registrations → Certificates & secrets.
          </p>
          <div className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 mt-3 ${t.border}`}>
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
            <p className={`text-sm ${t.body}`}>Enter the Secret VALUE, not the Secret ID.</p>
          </div>
          <p className={`text-xs mt-3 ${t.muted}`}>
            Client secrets expire. Selfic will notify administrators before the configured credential expires.
          </p>
          <div className="mt-3">
            <label className={labelCls}>Secret Expiration</label>
            <input
              type="date"
              name="entra-secret-expiration"
              value={state.secretExpiration}
              onChange={(e) => onChange({ secretExpiration: e.target.value })}
              className={inputCls}
              autoComplete="off"
            />
            <p className={`text-xs mt-1 ${t.muted}`}>Optional metadata. Not required for the connection test.</p>
          </div>
        </div>
      </EntraSection>

      <EntraSection
        title="Required Microsoft Graph Permissions"
        description="Your App Registration must have the following Application permissions:"
      >
        <div className={`inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs font-semibold mb-3 ${t.badgeAccent}`}>
          Permission Type: Application
        </div>
        <div className="space-y-3">
          <div>
            <CapabilityCheckList items={['User.Read.All']} />
            <p className={`text-xs mt-1 ml-6 ${t.muted}`}>Read users and user profile information</p>
          </div>
          <div>
            <CapabilityCheckList items={['Group.Read.All']} />
            <p className={`text-xs mt-1 ml-6 ${t.muted}`}>Read groups and group memberships</p>
          </div>
        </div>
        <p className={`text-xs mt-3 ${t.muted}`}>Admin consent must be granted for these permissions.</p>
      </EntraSection>

      <EntraSection
        title="Admin Consent"
        description="Before connecting, an administrator must grant admin consent to the Microsoft Graph permissions above."
      >
        <p className={`text-xs ${t.muted}`}>Status</p>
        <p className={`text-sm mt-1 ${t.title}`}>Not verified</p>
        <button type="button" onClick={() => setShowConsent(true)} className={'mt-3 text-sm font-medium ' + t.accentText}>
          View admin consent instructions
        </button>
      </EntraSection>

      <EntraSection
        title="Connection Test"
        description="Selfic will verify the credentials and required Microsoft Graph access."
      >
        {state.testStatus === 'auth_failed' ? (
          <div className={`rounded-lg border px-3 py-3 mb-3 ${t.errorBox}`}>
            <p className="text-sm font-medium">Authentication failed</p>
            <p className="text-xs mt-1">
              {state.testMessage || localError || 'Unable to authenticate the App Registration.'}
            </p>
            <p className="text-xs mt-2">Check: Tenant ID, Client ID, Client Secret</p>
          </div>
        ) : null}

        {state.testStatus === 'permissions_missing' ? (
          <div className={`rounded-lg border px-3 py-3 mb-3 ${t.border}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-500" />
              <div>
                <p className={`text-sm font-medium ${t.title}`}>Authentication successful</p>
                <p className={`text-xs mt-0.5 ${t.muted}`}>Required permissions are missing.</p>
              </div>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm">
              <li className={t.body}>✓ Authentication</li>
              <li className={t.body}>
                {state.graphAccess.users.ok ? '✓' : '✕'} Read users
              </li>
              <li className={t.body}>
                {state.graphAccess.groups.ok ? '✓' : '✕'} Read groups
              </li>
              <li className={t.body}>
                {state.graphAccess.groupMemberships.ok ? '✓' : '✕'} Read group memberships
              </li>
            </ul>
            <p className={`text-xs mt-3 ${t.muted}`}>
              Update the App Registration's Microsoft Graph permissions and grant admin consent.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" onClick={() => setShowConsent(true)} className={'px-3 py-1.5 rounded-md text-sm font-medium ' + t.btnSecondary}>
                View Instructions
              </button>
              <button type="button" onClick={runTest} className={'px-3 py-1.5 rounded-md text-sm font-medium ' + t.btnPrimary}>
                Test Again
              </button>
            </div>
          </div>
        ) : null}

        {localError && state.testStatus !== 'auth_failed' ? (
          <div className={`rounded-lg border px-3 py-2 text-xs mb-3 ${t.errorBox}`}>{localError}</div>
        ) : null}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={runTest}
            disabled={!canTest || state.testStatus === 'testing'}
            className={'px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ' + t.btnPrimary}
          >
            {state.testStatus === 'testing' ? 'Testing...' : state.testStatus === 'auth_failed' ? 'Test Again' : 'Test Connection'}
          </button>
        </div>
      </EntraSection>

      {showSetup ? (
        <InstructionModal title="App Registration setup" onClose={() => setShowSetup(false)}>
          <ol className={`list-decimal pl-5 space-y-2 text-sm ${t.body}`}>
            <li>Open the Microsoft Entra admin center.</li>
            <li>Go to App registrations and create a registration for Selfic.</li>
            <li>Copy the Directory (tenant) ID and Application (client) ID.</li>
            <li>Open API permissions and add Microsoft Graph Application permissions: User.Read.All and Group.Read.All.</li>
            <li>Select Grant admin consent for your organization.</li>
            <li>Open Certificates & secrets, create a client secret, and copy the Secret VALUE immediately.</li>
            <li>Return to Selfic and enter those values, then click Test Connection.</li>
          </ol>
        </InstructionModal>
      ) : null}

      {showConsent ? (
        <InstructionModal title="Grant Admin Consent" onClose={() => setShowConsent(false)}>
          <ol className={`list-decimal pl-5 space-y-2 text-sm ${t.body}`}>
            <li>Open Microsoft Entra admin center.</li>
            <li>Go to App registrations.</li>
            <li>Select your Selfic App Registration.</li>
            <li>Open API permissions.</li>
            <li>Add the required Microsoft Graph Application permissions.</li>
            <li>Select "Grant admin consent for your organization".</li>
            <li>Return to Selfic and click Test Connection.</li>
          </ol>
        </InstructionModal>
      ) : null}
    </>
  );
};

export default EntraConnectionStep;
